import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useEffect, useState } from 'react';
import Drawer from './components/Drawer';
import Home from './pages/Home';
import { ThemeProvider } from '@mui/material';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { Settings, ProductSelection } from './pages';
import Page404 from './pages/404';
import { AuthorizationAPI } from './apis';
import { ConfigProvider } from './components/ConfigContext';
import ErrorBoundary from './components/ErrorBoundary';
import Toast, { handleErrorModal } from './components/Toast';
import ApiErrorModal from './components/ApiErrorModal';
import { ToastContextProvider } from './components/ToastContext';
import './styles/globals.css';
import { theme } from './styles/theme';
import Product from './pages/Product';
import Cookies from 'js-cookie';
import useBeaconScript from './hooks/useBeaconScript';
import useDocumentTitle from './hooks/useDocumentTitle';
import { useRbacNotifications } from './hooks/useRbacNotifications';
import TermsOfUse from './components/TermsOfUse';
import { ApplicationsProvider } from './components/ApplicationContext';
import { ThemeDetailsProvider } from './components/ThemeDetailsContext';
import HelpCenterGuideViewer from './pages/HelpCenterGuideViewer/HelpCenterGuideViewer';
import { ROUTES } from './constants';
import { PermissionGate } from './components/PermissionGate';
import { ADMIN_ACCESS_ACTIONS, RBAC_ACTIONS } from './utils/permissionGateConfig';
import AccessUnavailable from './pages/AccessUnavailable';
import { clearCache } from './utils/indexedDBCache';
import CookieConsentLoader from './components/CookieConsentLoader';
export default function App() {
    const location = useLocation();
    const getAppTitleFromSession = (pathname) => {
        if (pathname === ROUTES.HOME || pathname === ROUTES.APPS) {
            return 'Converge Experience';
        }
        try {
            const currentApp = sessionStorage.getItem('current_application');
            return currentApp
                ? JSON.parse(currentApp)?.name || 'Converge Experience'
                : 'Converge Experience';
        }
        catch {
            return 'Converge Experience';
        }
    };
    const [appTitle, setAppTitle] = useState(() => getAppTitleFromSession(window.location.pathname));
    useEffect(() => {
        const handleAppChange = () => setAppTitle(getAppTitleFromSession(location.pathname));
        window.addEventListener('current_application_changed', handleAppChange);
        return () => window.removeEventListener('current_application_changed', handleAppChange);
    }, [location.pathname]);
    useBeaconScript(appTitle);
    useDocumentTitle();
    useRbacNotifications();
    const navigate = useNavigate();
    // Check React version compatibility on app initialization
    useEffect(() => {
        // Clear any existing reload counters to prevent stuck reload loops
        sessionStorage.removeItem('mf_reload_count');
        // checkReactVersionCompatibility()
    }, []);
    const timeout = 60 * 60 * 1000; // 1 hour timeout
    const throttle = 10 * 60 * 1000; // 10 minute throttle
    let refreshPromise = null;
    const isFirstAction = useRef(true);
    const performTokenRefresh = async () => {
        if (!refreshPromise) {
            refreshPromise = AuthorizationAPI.refreshToken()
                .then((result) => {
                if (result?.accessToken) {
                    Cookies.set('access_token', result.accessToken, {
                        secure: true,
                        sameSite: 'Strict'
                    });
                    Cookies.set('refresh_token', result.refreshToken, {
                        secure: true,
                        sameSite: 'Strict'
                    });
                }
                return result;
            })
                .finally(() => {
                refreshPromise = null;
            });
        }
        return refreshPromise;
    };
    const onIdle = async () => {
        handleErrorModal(true, 'You were logged out due to inactivity.');
        try {
            await clearCache();
        }
        catch (error) {
            console.error('Failed to clear IndexedDB cache on idle logout:', error);
        }
        try {
            await AuthorizationAPI.logout();
        }
        catch (error) {
            console.error('Failed to logout on idle:', error);
        }
        navigate('/');
    };
    const onAction = async () => {
        if (isFirstAction.current) {
            isFirstAction.current = false;
            return;
        }
        try {
            const result = await performTokenRefresh();
            if (result?.accessToken) {
                activate(); // reset the idle timer
            }
            else {
                // logout on token refresh failure
                try {
                    await clearCache();
                }
                catch (error) {
                    console.error('Failed to clear IndexedDB cache on token refresh failure:', error);
                }
                try {
                    await AuthorizationAPI.logout();
                }
                catch (error) {
                    console.error('Failed to logout after token refresh failure:', error);
                }
                navigate('/');
            }
        }
        catch (error) {
            console.error('Token refresh error:', error);
        }
    };
    const { activate } = useIdleTimer({
        onIdle,
        onAction,
        onActive: onAction,
        timeout,
        throttle
    });
    return (_jsx(ErrorBoundary, { children: _jsx(ApplicationsProvider, { children: _jsxs(ConfigProvider, { children: [_jsx(CookieConsentLoader, {}), _jsx(ThemeDetailsProvider, { children: _jsx(ThemeProvider, { theme: theme, children: _jsxs(ToastContextProvider, { children: [_jsxs(Routes, { children: [_jsx(Route, { path: ROUTES.HOME, element: _jsx(Home, {}) }), JSON.parse(process.env
                                                .REACT_APP_SHOW_PRODUCT_SELECTION_PAGE ??
                                                'false') && (_jsx(Route, { path: ROUTES.APPS, element: _jsx(Drawer, { children: _jsx(PermissionGate, { action: RBAC_ACTIONS.RBAC_VIEW_APPLICATION, fallback: _jsx(AccessUnavailable, {}), requireAll: false, children: _jsx(ProductSelection, {}) }) }) })), _jsx(Route, { path: ROUTES.PRODUCT, element: _jsx(Drawer, { children: _jsx(Product, {}) }) }), _jsx(Route, { path: ROUTES.SETTINGS, element: _jsx(Drawer, { children: _jsx(PermissionGate, { action: ADMIN_ACCESS_ACTIONS, fallback: _jsx(AccessUnavailable, {}), children: _jsx(Settings, {}) }) }) }), _jsx(Route, { path: ROUTES.TERMS_OF_USE, element: _jsx(TermsOfUse, {}) }), _jsx(Route, { path: ROUTES.HELP, element: _jsx(Drawer, { children: _jsx(HelpCenterGuideViewer, {}) }) }), _jsx(Route, { path: "*", element: _jsx(Page404, {}) })] }), _jsx(Toast, {}), _jsx(ApiErrorModal, {})] }) }) })] }) }) }));
}
